// src/views/reports/ExpenseReportView.js
import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom'; 
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, 
    PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';
import { 
    TrendingUp, DollarSign, Users, Briefcase, 
    Calendar, ArrowUpRight, ArrowDownRight, Loader2, XCircle, FileText, ArrowLeft 
} from 'lucide-react';
import { startOfYear, format, subMonths, isSameMonth, startOfMonth } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';

// --- COLORS FOR CHARTS ---
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// --- INTERACTIVE KPI CARD COMPONENT ---
const ReportKpi = ({ title, amount, trend, trendLabel, icon, colorClass, onClick, isActive }) => (
    <div 
        onClick={onClick}
        className={`bg-white p-6 rounded-lg border shadow-sm transition-all duration-200 
        ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300' : ''}
        ${isActive ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200' : ''}`}
    >
        <div className="flex justify-between items-start mb-4">
            <div>
                <p className={`text-sm font-medium uppercase ${isActive ? 'text-blue-700' : 'text-gray-500'}`}>{title}</p>
                <h3 className={`text-3xl font-bold mt-1 ${isActive ? 'text-blue-900' : 'text-gray-800'}`}>{formatCurrency(amount)}</h3>
            </div>
            <div className={`p-3 rounded-full ${isActive ? 'bg-white' : colorClass.replace('text-', 'bg-').replace('600', '100')}`}>
                {icon}
            </div>
        </div>
        {trend !== null && (
            <div className={`flex items-center text-sm ${trend >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                {trend >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                <span className="font-bold ml-1">{Math.abs(trend)}%</span>
                <span className={`ml-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>{trendLabel}</span>
            </div>
        )}
    </div>
);

const ExpenseReportView = ({ currentUserProfile }) => {
    // 1. Hooks
    const [expenses, setExpenses] = useState([]);
    const [users, setUsers] = useState({});
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('YTD'); // MTD, 90D, YTD, ALL
    const [drillDown, setDrillDown] = useState(null); 

    // Current Month Helper
    const currentMonthLabel = useMemo(() => format(new Date(), 'MMM yyyy'), []);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const fetchData = async () => {
            // A. Fetch Expenses
            try {
                const expensesRef = collection(db, `organizations/${orgId}/expenses`);
                const expSnapshot = await getDocs(expensesRef);
                const expData = expSnapshot.docs.map(doc => {
                    const d = doc.data();
                    return {
                        id: doc.id,
                        ...d,
                        amount: parseFloat(d.amount) || 0,
                        dateObj: d.date?.toDate ? d.date.toDate() : new Date()
                    };
                });
                setExpenses(expData);
            } catch (error) {
                console.error("Error loading expenses:", error);
            }

            // B. Fetch Users (Silent)
            try {
                // Modified check to allow Owner roles here as well for fetching user names
                const allowedRoles = ['Admin', 'Manager', 'Owner', 'Owner/System Admin', 'Owner / System Admin', 'System Admin'];
                if (allowedRoles.includes(currentUserProfile.role)) {
                    const usersRef = collection(db, 'users');
                    const qUsers = query(usersRef, where('organizationId', '==', orgId));
                    const userSnapshot = await getDocs(qUsers);
                    const userMap = {};
                    userSnapshot.docs.forEach(doc => {
                        userMap[doc.id] = doc.data().name || 'Unknown User';
                    });
                    setUsers(userMap);
                }
            } catch (error) { }

            setLoading(false);
        };

        fetchData();
    }, [currentUserProfile]);

    // 2. Metrics & Filtering
    const { metrics, drillDownData } = useMemo(() => {
        const now = new Date();
        let filtered = expenses;

        // A. Time Range Filter
        if (timeRange === 'MTD') {
            const start = startOfMonth(now);
            filtered = expenses.filter(e => e.dateObj >= start);
        } else if (timeRange === 'YTD') {
            const start = startOfYear(now);
            filtered = expenses.filter(e => e.dateObj >= start);
        } else if (timeRange === '90D') {
            const start = subMonths(now, 3);
            filtered = expenses.filter(e => e.dateObj >= start);
        }

        // B. Drill Down Filter (If Active)
        let drillDownList = [];
        if (drillDown) {
            drillDownList = filtered.filter(e => {
                if (drillDown.type === 'category') return e.category === drillDown.value;
                if (drillDown.type === 'vendor') return e.vendor === drillDown.value;
                if (drillDown.type === 'user') {
                    const userName = users[e.createdBy] || 'System/Admin';
                    return userName === drillDown.value;
                }
                if (drillDown.type === 'month') {
                    return format(e.dateObj, 'MMM yyyy') === drillDown.value;
                }
                return true;
            });
        }

        // C. Aggregations
        const totalSpend = filtered.reduce((sum, e) => sum + e.amount, 0);
        
        const currentMonth = filtered.filter(e => isSameMonth(e.dateObj, now));
        const lastMonth = filtered.filter(e => isSameMonth(e.dateObj, subMonths(now, 1)));
        
        const curMonthSum = currentMonth.reduce((s, e) => s + e.amount, 0);
        const lastMonthSum = lastMonth.reduce((s, e) => s + e.amount, 0);
        let momGrowth = 0;
        if (lastMonthSum > 0) {
            momGrowth = ((curMonthSum - lastMonthSum) / lastMonthSum) * 100;
        }

        const vendorMap = {};
        filtered.forEach(e => {
            const v = e.vendor || 'Unknown';
            vendorMap[v] = (vendorMap[v] || 0) + e.amount;
        });
        const topVendors = Object.entries(vendorMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);

        const userSpendMap = {};
        filtered.forEach(e => {
            const uid = e.createdBy || 'unknown';
            const name = users[uid] || 'System/Admin';
            userSpendMap[name] = (userSpendMap[name] || 0) + e.amount;
        });
        const userSpendData = Object.entries(userSpendMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);

        const catMap = {};
        filtered.forEach(e => {
            const c = e.category || 'Uncategorized';
            catMap[c] = (catMap[c] || 0) + e.amount;
        });
        const categoryData = Object.entries(catMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);

        const trendMap = {};
        filtered.forEach(e => {
            const key = format(e.dateObj, 'MMM yyyy');
            trendMap[key] = (trendMap[key] || 0) + e.amount;
        });
        const trendData = Object.entries(trendMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => new Date(a.name) - new Date(b.name));

        return { 
            metrics: { totalSpend, momGrowth, topVendors, userSpendData, categoryData, trendData, curMonthSum },
            drillDownData: drillDownList
        };
    }, [expenses, users, timeRange, drillDown]);

    // 3. Robust Handlers
    const handleTrendClick = (e) => {
        if (!e) return;
        if (e.activeLabel) {
            setDrillDown({ type: 'month', value: e.activeLabel });
            return;
        }
        if (e.activePayload && e.activePayload.length > 0) {
            setDrillDown({ type: 'month', value: e.activePayload[0].payload.name });
        }
    };

    const handleUserClick = (e) => {
        if (e && e.activePayload && e.activePayload.length > 0) {
            setDrillDown({ type: 'user', value: e.activePayload[0].payload.name });
        } else if (e && e.activeLabel) {
            setDrillDown({ type: 'user', value: e.activeLabel });
        }
    };

    // 4. Security & Loading
    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    
    if (loading) return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            {/* HEADER - ADDED BACK ARROW LOGIC */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Expense Analysis</h1>
                        <p className="text-gray-500 mt-1">Deep dive into organization spending</p>
                    </div>
                </div>
                
                {/* RANGE TOGGLE */}
                <div className="bg-white border rounded-md shadow-sm p-1 flex">
                    {['MTD', '90D', 'YTD', 'ALL'].map((r) => (
                        <button
                            key={r}
                            onClick={() => { setTimeRange(r); setDrillDown(null); }}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                timeRange === r ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <ReportKpi 
                    title={`Total Spend (${timeRange})`} 
                    amount={metrics.totalSpend} 
                    trend={null}
                    icon={<DollarSign size={24} />} 
                    colorClass="text-blue-600"
                    onClick={() => setDrillDown(null)} 
                    isActive={!drillDown}
                />
                <ReportKpi 
                    title="Spend This Month" 
                    amount={metrics.curMonthSum} 
                    trend={metrics.momGrowth.toFixed(1)} 
                    trendLabel="vs last month"
                    icon={<Calendar size={24} />} 
                    colorClass="text-purple-600"
                    onClick={() => setDrillDown({ type: 'month', value: currentMonthLabel })}
                    isActive={drillDown?.type === 'month' && drillDown?.value === currentMonthLabel}
                />
                <ReportKpi 
                    title="Top Category" 
                    amount={metrics.categoryData[0]?.value || 0} 
                    trend={null} 
                    trendLabel={metrics.categoryData[0]?.name}
                    icon={<Briefcase size={24} />} 
                    colorClass="text-orange-600"
                    onClick={() => {
                        if (metrics.categoryData[0]) {
                            setDrillDown({ type: 'category', value: metrics.categoryData[0].name });
                        }
                    }}
                    isActive={drillDown?.type === 'category' && drillDown?.value === metrics.categoryData[0]?.name}
                />
            </div>

            {/* CHARTS ROW 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                
                {/* 1. MONTHLY TREND */}
                <div className="bg-white p-6 rounded-lg border shadow-sm min-w-0 overflow-hidden relative group">
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium pointer-events-none">Click data points to drill down</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUp size={20} className="text-blue-600"/> Spending Trend
                    </h3>
                    <div className="h-72 w-full cursor-pointer">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={metrics.trendData} onClick={handleTrendClick}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val/1000}k`}/>
                                <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                                <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={3} activeDot={{ r: 8, onClick: (e, payload) => setDrillDown({ type: 'month', value: payload.payload.name }) }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. SPEND BY USER */}
                <div className="bg-white p-6 rounded-lg border shadow-sm min-w-0 overflow-hidden relative group">
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium pointer-events-none">Click bars to drill down</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Users size={20} className="text-purple-600"/> Spend by User
                    </h3>
                    <div className="h-72 w-full cursor-pointer">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={metrics.userSpendData} layout="vertical" margin={{ left: 20 }} onClick={handleUserClick}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" hide />
                                <YAxis type="category" dataKey="name" width={100} fontSize={12} />
                                <RechartsTooltip formatter={(val) => formatCurrency(val)} cursor={{fill: 'transparent'}} />
                                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} onClick={(data) => setDrillDown({ type: 'user', value: data.name })} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* CHARTS ROW 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* 3. CATEGORY PIE */}
                <div className="bg-white p-6 rounded-lg border shadow-sm lg:col-span-1 min-w-0 overflow-hidden relative group">
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium pointer-events-none">Click slice to filter</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Category Breakdown</h3>
                    <div className="h-64 w-full cursor-pointer">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={metrics.categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" onClick={(data) => setDrillDown({ type: 'category', value: data.name })}>
                                    {metrics.categoryData.map((entry, index) => ( <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} /> ))}
                                </Pie>
                                <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                                <Legend verticalAlign="bottom" height={36}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 4. TOP VENDORS TABLE */}
                <div className="bg-white p-6 rounded-lg border shadow-sm lg:col-span-2 min-w-0 overflow-hidden">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Top 5 Vendors</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-4 py-3 font-semibold text-gray-600">Vendor Name</th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-right">Total Spent</th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-right">% of Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {metrics.topVendors.length > 0 ? metrics.topVendors.map((vendor, idx) => (
                                    <tr key={idx} onClick={() => setDrillDown({ type: 'vendor', value: vendor.name })} className="hover:bg-blue-50 cursor-pointer transition-colors" title="Click to see vendor details">
                                        <td className="px-4 py-3 font-medium text-gray-800">{vendor.name}</td>
                                        <td className="px-4 py-3 text-right font-bold text-gray-800">{formatCurrency(vendor.value)}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">{metrics.totalSpend > 0 ? ((vendor.value / metrics.totalSpend) * 100).toFixed(1) + '%' : '0%'}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="3" className="text-center py-4 text-gray-500">No data available</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* --- DRILL DOWN SECTION --- */}
            {drillDown && (
                <div className="mt-8 bg-white border border-blue-200 rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="bg-blue-50 px-6 py-4 flex justify-between items-center border-b border-blue-100">
                        <div className="flex items-center gap-2">
                            <FileText className="text-blue-600" size={20} />
                            <h3 className="text-lg font-bold text-gray-800">
                                Drill Down: <span className="text-blue-600">{drillDown.value}</span>
                            </h3>
                            <span className="text-xs bg-white border border-blue-200 text-blue-500 px-2 py-0.5 rounded uppercase font-semibold">
                                {drillDown.type}
                            </span>
                        </div>
                        <button onClick={() => setDrillDown(null)} className="text-gray-400 hover:text-red-500 hover:bg-white rounded-full p-1 transition-all"><XCircle size={24} /></button>
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 border-b sticky top-0">
                                <tr>
                                    <th className="px-6 py-3 font-semibold text-gray-600">Date</th>
                                    <th className="px-6 py-3 font-semibold text-gray-600">Vendor</th>
                                    <th className="px-6 py-3 font-semibold text-gray-600">Category</th>
                                    <th className="px-6 py-3 font-semibold text-gray-600">User</th>
                                    <th className="px-6 py-3 font-semibold text-gray-600 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {drillDownData.length === 0 ? (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No records found.</td></tr>
                                ) : (
                                    drillDownData.map((exp) => (
                                        <tr key={exp.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-3">{format(exp.dateObj, 'MMM d, yyyy')}</td>
                                            <td className="px-6 py-3 font-medium">{exp.vendor}</td>
                                            <td className="px-6 py-3"><span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{exp.category}</span></td>
                                            <td className="px-6 py-3 text-gray-500">{users[exp.createdBy] || 'System'}</td>
                                            <td className="px-6 py-3 text-right font-bold">{formatCurrency(exp.amount)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-right text-xs text-gray-500 font-medium">
                        Showing {drillDownData.length} transaction{drillDownData.length !== 1 && 's'}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseReportView;